// Bulk SMS composer for Active Client Portals — compose one message, auto-personalise
// per client, preview, then send. Each client gets their OWN portal link.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, MessageSquare, Eye, Send, ChevronLeft, Loader2, AlertTriangle, CheckCircle2, Trash2, Save, ChevronDown, Plus, Smartphone, CalendarClock, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { token: '{{FIRST_NAME}}', label: 'First name' },
  { token: '{{FULL_NAME}}', label: 'Full name' },
  { token: '{{PORTAL_LINK}}', label: 'Portal link' },
  { token: '{{PORTAL_EXPIRY}}', label: 'Portal expiry date' },
];

// Default = Active Client Portals. E-Sign passes ESIGN_SMS_CONFIG — same composer, different recipients/links.
export const PORTAL_SMS_CONFIG = {
  noun: 'portals', fields: FIELDS, idKey: 'applicant_id', allowSchedule: true, templateScope: null,
  toPayload: (r) => ({ app_id: r.app_id, applicant_id: r.applicant_id }),
  endpoints: { preview: '/api/onboarding/bulk-sms/preview', send: '/api/onboarding/bulk-sms/send', schedule: '/api/onboarding/bulk-sms/schedule', templates: '/api/onboarding/sms-templates' },
  placeholder: 'Hi {{FIRST_NAME}}, just a quick reminder that your client portal is due to expire. Please complete it here: {{PORTAL_LINK}}',
  emptyHint: 'No recipients — close and select some portals.',
};
export const ESIGN_SMS_CONFIG = {
  noun: 'signers', idKey: 'signer_id', allowSchedule: true, templateScope: 'esign',
  fields: [
    { token: '{{FIRST_NAME}}', label: 'First name' }, { token: '{{FULL_NAME}}', label: 'Full name' }, { token: '{{ESIGN_LINK}}', label: 'E-Sign link' },
    { token: '{{BROKER_NAME}}', label: 'Broker name' }, { token: '{{DOCUMENT_TITLE}}', label: 'Document title' }, { token: '{{EXPIRY_DATE}}', label: 'Link expiry' },
  ],
  toPayload: (r) => ({ doc_id: r.doc_id, signer_id: r.signer_id }),
  endpoints: { preview: '/api/esign/bulk-sms/preview', send: '/api/esign/bulk-sms/send', schedule: '/api/esign/bulk-sms/schedule', templates: '/api/esign/bulk-sms/templates' },
  placeholder: 'Hi {{FIRST_NAME}}, just a quick reminder that we are still waiting for your electronic signature to progress your finance application.\nYou can review and sign your documents securely here: {{ESIGN_LINK}}\nThanks, {{BROKER_NAME}}',
  emptyHint: 'No recipients — close and select signing requests with outstanding signatures.',
};

export const linkify = (text, idPrefix = 'bulk-sms-preview-link') => String(text || '').split(/(https?:\/\/[^\s]+)/g).map((part, i) => (
  /^https?:\/\//.test(part)
    ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" data-testid={`${idPrefix}-${i}`} className="font-semibold text-indigo-600 underline decoration-indigo-300 underline-offset-2 break-all hover:text-indigo-700">{part}</a>
    : <React.Fragment key={i}>{part}</React.Fragment>
));

const segInfo = (text) => {
  const len = text.length;
  const unicode = /[^\u0000-\u007F]/.test(text);
  const limit = unicode ? 70 : 160;
  const per = unicode ? 67 : 153;
  const segments = len === 0 ? 0 : len <= limit ? 1 : Math.ceil(len / per);
  return { len, segments, unicode };
};

export default function BulkSmsComposer({ open, onClose, recipients, noMobileCount = 0, headers, onSent, config = PORTAL_SMS_CONFIG }) {
  const { fields: FIELDS, idKey, toPayload, endpoints, placeholder, allowSchedule, templateScope, emptyHint } = config;
  const idOf = (r) => r[idKey];
  const [step, setStep] = useState('compose'); // compose | preview | results
  const [body, setBody] = useState('');
  const [recips, setRecips] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [tplMenu, setTplMenu] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [newTplName, setNewTplName] = useState('');
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduled, setScheduled] = useState(null);
  const taRef = useRef(null);
  const selRef = useRef(null);
  const idemRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep('compose'); setResults(null); setPreviews([]); setActiveTemplate(null); setShowSaveTpl(false);
    setScheduleMode(false); setScheduleAt(''); setScheduled(null);
    setRecips(recipients || []);
    idemRef.current = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `bulk-${Date.now()}-${Math.random()}`;
    axios.get(`${API}${endpoints.templates}`, { headers }).then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, [open, recipients, headers, endpoints.templates]);

  const insertField = useCallback((token) => {
    const ta = taRef.current;
    const pos = selRef.current ?? (ta ? ta.selectionStart : body.length);
    const next = body.slice(0, pos) + token + body.slice(pos);
    setBody(next);
    requestAnimationFrame(() => { if (ta) { ta.focus(); const c = pos + token.length; ta.setSelectionRange(c, c); selRef.current = c; } });
  }, [body]);

  const removeRecip = (id) => setRecips((r) => r.filter((x) => idOf(x) !== id));

  const loadTemplate = (t) => { setBody(t.body || ''); setActiveTemplate(t); setTplMenu(false); };
  const deleteTemplate = async (t, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await axios.delete(`${API}/api/onboarding/sms-templates/${t.id}`, { headers }); setTemplates((x) => x.filter((y) => y.id !== t.id)); if (activeTemplate?.id === t.id) setActiveTemplate(null); toast.success('Template deleted'); }
    catch { toast.error('Could not delete template'); }
  };
  const saveNewTemplate = async () => {
    const name = newTplName.trim();
    if (!name) { toast.error('Give the template a name'); return; }
    setSavingTpl(true);
    try { const { data } = await axios.post(`${API}/api/onboarding/sms-templates`, { name, body, scope: templateScope }, { headers }); setTemplates((x) => [...x, data]); setActiveTemplate(data); setShowSaveTpl(false); setNewTplName(''); toast.success('Template saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save template'); }
    finally { setSavingTpl(false); }
  };
  const updateTemplate = async () => {
    if (!activeTemplate) return;
    setSavingTpl(true);
    try { const { data } = await axios.patch(`${API}/api/onboarding/sms-templates/${activeTemplate.id}`, { body }, { headers }); setTemplates((x) => x.map((y) => (y.id === data.id ? data : y))); setActiveTemplate(data); toast.success('Template updated'); }
    catch { toast.error('Could not update template'); }
    finally { setSavingTpl(false); }
  };

  const goPreview = async () => {
    if (!body.trim()) { toast.error('Write a message first'); return; }
    if (!recips.length) { toast.error('No recipients selected'); return; }
    setLoadingPreview(true);
    try {
      const { data } = await axios.post(`${API}${endpoints.preview}`, { body, recipients: recips.map(toPayload), base_url: window.location.origin }, { headers });
      setPreviews(data.previews || []); setStep('preview');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate preview'); }
    finally { setLoadingPreview(false); }
  };

  const doSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}${endpoints.send}`, {
        body, recipients: recips.map(toPayload), base_url: window.location.origin,
        idempotency_key: idemRef.current, template_id: activeTemplate?.id || null, template_name: activeTemplate?.name || null,
      }, { headers });
      setResults(data); setStep('results'); onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk send failed'); }
    finally { setSending(false); }
  };

  const doSchedule = async () => {
    if (sending) return;
    if (!scheduleAt) { toast.error('Pick a date and time'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}${endpoints.schedule}`, {
        body, recipients: recips.map(toPayload),
        idempotency_key: idemRef.current, template_id: activeTemplate?.id || null, template_name: activeTemplate?.name || null,
        scheduled_at_local: scheduleAt,
      }, { headers });
      setScheduled(data); setStep('results'); onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not schedule'); }
    finally { setSending(false); }
  };

  const minSchedule = useMemo(() => {
    const d = new Date(Date.now() + 2 * 60000);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }, [open]);

  const { len, segments } = useMemo(() => segInfo(body), [body]);
  const invalidCount = recips.filter((r) => !r.mobile_valid).length;
  const sendablePreviewCount = previews.filter((p) => p.sendable).length;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4" data-testid="bulk-sms-composer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2.5">
            {step !== 'compose' && step !== 'results' && <button type="button" onClick={() => setStep('compose')} data-testid="bulk-sms-back" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>}
            <span className="flex items-center gap-2 font-black text-slate-900"><MessageSquare className="w-4 h-4 text-[#15133A]" /> {step === 'results' ? 'Send complete' : step === 'preview' ? 'Preview messages' : 'Send bulk SMS'}</span>
          </div>
          <button type="button" onClick={onClose} data-testid="bulk-sms-close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {step === 'compose' && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <button type="button" onClick={() => setTplMenu((v) => !v)} data-testid="bulk-sms-templates-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">Templates <ChevronDown className="w-3.5 h-3.5" /></button>
                {tplMenu && (
                  <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl" data-testid="bulk-sms-templates-menu" onMouseLeave={() => setTplMenu(false)}>
                    {!templates.length ? <p className="px-2 py-2 text-xs text-slate-400">No saved templates yet</p> : templates.map((t) => (
                      <div key={t.id} className="group flex items-center gap-1 rounded-lg hover:bg-slate-100">
                        <button type="button" onClick={() => loadTemplate(t)} data-testid={`bulk-sms-template-${t.id}`} className="flex-1 px-2 py-1.5 text-left text-[13px] font-medium text-slate-800 truncate">{t.name}</button>
                        <button type="button" onClick={(e) => deleteTemplate(t, e)} data-testid={`bulk-sms-template-delete-${t.id}`} className="p-1 text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative ml-auto">
                <select onChange={(e) => { if (e.target.value) { insertField(e.target.value); e.target.value = ''; } }} data-testid="bulk-sms-insert-field" defaultValue="" className="appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-8 py-2 text-xs font-bold text-slate-700 outline-none hover:bg-slate-50 cursor-pointer">
                  <option value="" disabled>Insert field…</option>
                  {FIELDS.map((f) => <option key={f.token} value={f.token}>{f.label}</option>)}
                </select>
                <Plus className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              </div>
            </div>

            <div>
              <textarea ref={taRef} value={body} onChange={(e) => setBody(e.target.value)} onSelect={(e) => { selRef.current = e.target.selectionStart; }} onKeyUp={(e) => { selRef.current = e.target.selectionStart; }} onClick={(e) => { selRef.current = e.target.selectionStart; }}
                rows={7} data-testid="bulk-sms-body" placeholder={placeholder}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-800 outline-none focus:border-slate-900 focus:bg-white resize-none" />
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                <span data-testid="bulk-sms-charcount"><b className="text-slate-700 tabular-nums">{len}</b> chars</span>
                <span data-testid="bulk-sms-segcount"><b className="text-slate-700 tabular-nums">{segments}</b> SMS segment{segments === 1 ? '' : 's'}</span>
                <span className="inline-flex flex-wrap gap-1">{FIELDS.map((f) => <button key={f.token} type="button" onClick={() => insertField(f.token)} data-testid={`bulk-sms-chip-${f.label.replace(/\s+/g, '-').toLowerCase()}`} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 hover:bg-indigo-100">+ {f.label}</button>)}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!showSaveTpl ? (
                <>
                  <button type="button" onClick={() => setShowSaveTpl(true)} data-testid="bulk-sms-save-template" disabled={!body.trim()} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40"><Save className="w-3.5 h-3.5" /> Save as template</button>
                  {activeTemplate && <button type="button" onClick={updateTemplate} disabled={savingTpl} data-testid="bulk-sms-update-template" className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:opacity-40">Update “{activeTemplate.name}”</button>}
                </>
              ) : (
                <div className="flex items-center gap-2 w-full">
                  <input autoFocus value={newTplName} onChange={(e) => setNewTplName(e.target.value)} placeholder="Template name" data-testid="bulk-sms-new-template-name" className="flex-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-slate-900" />
                  <button type="button" onClick={saveNewTemplate} disabled={savingTpl} data-testid="bulk-sms-new-template-save" className="rounded-xl bg-[#15133A] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40">{savingTpl ? 'Saving…' : 'Save'}</button>
                  <button type="button" onClick={() => { setShowSaveTpl(false); setNewTplName(''); }} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600">Cancel</button>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5 border-b border-slate-100">
                <span className="text-xs font-black text-slate-700">Recipients · <span data-testid="bulk-sms-recipient-count">{recips.length}</span></span>
                {invalidCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> {invalidCount} invalid mobile</span>}
              </div>
              <ul className="max-h-48 overflow-y-auto divide-y divide-slate-50">
                {recips.map((r) => (
                  <li key={idOf(r)} className="flex items-center gap-2 px-4 py-2" data-testid={`bulk-sms-recipient-${idOf(r)}`}>
                    <span className="min-w-0 flex-1"><span className="text-sm font-semibold text-slate-800 truncate">{r.full_name || r.first_name}</span><span className="ml-2 text-[11px] text-slate-400">{r.reference}{r.title ? ` · ${r.title}` : ''}</span></span>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${r.mobile_valid ? 'text-slate-500' : 'text-amber-600'}`}><Smartphone className="w-3 h-3" />{r.mobile || 'no mobile'}</span>
                    <button type="button" onClick={() => removeRecip(idOf(r))} data-testid={`bulk-sms-remove-${idOf(r)}`} className="rounded-full p-1 text-slate-300 hover:text-rose-500"><X className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
                {!recips.length && <li className="px-4 py-6 text-center text-xs text-slate-400">{emptyHint}</li>}
              </ul>
              {noMobileCount > 0 && <div className="bg-amber-50 px-4 py-2 text-[11px] font-medium text-amber-700 border-t border-amber-100">{noMobileCount} selected {noMobileCount === 1 ? 'person has' : 'people have'} no mobile on file and {noMobileCount === 1 ? 'was' : 'were'} skipped.</div>}
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="bulk-sms-preview-list">
            <p className="text-xs text-slate-500">Showing exactly what each client will receive. <b className="text-slate-700">{sendablePreviewCount}</b> of {previews.length} ready to send.</p>
            {previews.map((p) => (
              <div key={idOf(p)} className={`rounded-2xl border p-3.5 ${p.sendable ? 'border-slate-200 bg-white' : 'border-amber-200 bg-amber-50/60'}`} data-testid={`bulk-sms-preview-${idOf(p)}`}>
                <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-black text-slate-900">{p.full_name || p.first_name}</span><span className="text-[11px] text-slate-400">{p.mobile || 'no mobile'} · {p.reference}</span></div>
                <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{linkify(p.message, `bulk-sms-preview-link-${idOf(p)}`)}</p>
                {p.issue && <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> {p.issue}</p>}
                {!p.issue && p.warning && <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-slate-500" data-testid={`bulk-sms-preview-warning-${idOf(p)}`}><Clock className="w-3.5 h-3.5" /> {p.warning} — sending again anyway</p>}
              </div>
            ))}
          </div>
        )}

        {step === 'results' && scheduled && (
          <div className="flex-1 overflow-y-auto px-5 py-8 text-center" data-testid="bulk-sms-scheduled-confirm">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-indigo-50"><CalendarClock className="h-7 w-7 text-[#15133A]" /></div>
            <h3 className="text-lg font-black text-slate-900">Scheduled</h3>
            <p className="mt-1 text-sm text-slate-600">Your bulk SMS to <b>{scheduled.recipient_count ?? recips.length}</b> {(scheduled.recipient_count ?? recips.length) === 1 ? (config.noun === 'signers' ? 'signer' : 'client') : (config.noun === 'signers' ? 'signers' : 'clients')} will send on</p>
            <p className="mt-1 text-base font-black text-[#15133A]" data-testid="bulk-sms-scheduled-label">{scheduled.scheduled_at_label}</p>
            <p className="mt-3 text-xs text-slate-400">Each message is personalised and the links are generated fresh when it sends{config.noun === 'signers' ? ' — anyone who signs before then is skipped automatically' : ''}. You can cancel it from “Scheduled” on the {config.noun} list.</p>
          </div>
        )}

        {step === 'results' && results && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="bulk-sms-results">
            <div className="grid grid-cols-3 gap-2.5">
              <div className="rounded-2xl bg-slate-50 p-3 text-center"><p className="text-2xl font-black text-slate-900 tabular-nums" data-testid="bulk-sms-result-selected">{results.selected}</p><p className="text-[11px] font-bold text-slate-500">Selected</p></div>
              <div className="rounded-2xl bg-emerald-50 p-3 text-center"><p className="text-2xl font-black text-emerald-700 tabular-nums" data-testid="bulk-sms-result-sent">{results.sent}</p><p className="text-[11px] font-bold text-emerald-600">{results.dry_run ? 'Simulated' : 'Sent'}</p></div>
              <div className="rounded-2xl bg-rose-50 p-3 text-center"><p className="text-2xl font-black text-rose-600 tabular-nums" data-testid="bulk-sms-result-failed">{results.failed}</p><p className="text-[11px] font-bold text-rose-500">Failed</p></div>
            </div>
            {results.dry_run && <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-700">SMS provider is in dry-run mode — messages were recorded but not actually delivered.</p>}
            <ul className="space-y-1.5">
              {results.results.map((r) => (
                <li key={idOf(r)} className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2" data-testid={`bulk-sms-result-row-${idOf(r)}`}>
                  {r.status === 'delivered' ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : r.status === 'dry_run' ? <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />}
                  <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800 truncate">{r.name || r.first_name}</span>
                  <span className="text-[11px] text-slate-400">{r.mobile}</span>
                  {r.failure_reason && <span className="text-[11px] font-medium text-rose-600 truncate max-w-[40%]">{r.failure_reason}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="shrink-0 border-t border-slate-100 px-5 py-3.5 flex flex-wrap items-center justify-between gap-2.5">
          {step === 'compose' && <span />}
          {step === 'compose' && <button type="button" onClick={goPreview} disabled={loadingPreview || !recips.length} data-testid="bulk-sms-preview-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-[#15133A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1E1B52] disabled:opacity-40">{loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview</button>}
          {step === 'preview' && !allowSchedule && <span />}
          {step === 'preview' && allowSchedule && (
            <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer" data-testid="bulk-sms-schedule-toggle">
              <input type="checkbox" checked={scheduleMode} onChange={(e) => setScheduleMode(e.target.checked)} className="h-4 w-4 rounded accent-[#15133A]" />
              <CalendarClock className="w-3.5 h-3.5" /> Schedule for later
            </label>
          )}
          {step === 'preview' && (
            <div className="flex items-center gap-2 ml-auto">
              {scheduleMode && (
                <div className="flex items-center gap-1.5">
                  <input type="datetime-local" value={scheduleAt} min={minSchedule} onChange={(e) => setScheduleAt(e.target.value)} data-testid="bulk-sms-schedule-at"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-900" />
                  <span className="text-[10px] font-bold text-slate-400">AET</span>
                </div>
              )}
              {scheduleMode ? (
                <button type="button" onClick={doSchedule} disabled={sending || !sendablePreviewCount || !scheduleAt} data-testid="bulk-sms-schedule-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-[#15133A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1E1B52] disabled:opacity-40">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Schedule</button>
              ) : (
                <button type="button" onClick={doSend} disabled={sending || !sendablePreviewCount} data-testid="bulk-sms-send-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-40">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {sendablePreviewCount} {sendablePreviewCount === 1 ? 'client' : 'clients'}</button>
              )}
            </div>
          )}
          {step === 'results' && <span />}
          {step === 'results' && <button type="button" onClick={onClose} data-testid="bulk-sms-done-btn" className="ml-auto rounded-xl bg-[#15133A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1E1B52]">Done</button>}
        </div>
      </div>
    </div>
  );
}
